import React, { useEffect, useState } from 'react';
import { Tree, Empty, Spin, Tabs, Button, message } from 'antd';
import {
    FilePdfOutlined,
    FolderOutlined,
    WifiOutlined,
    ThunderboltOutlined,
    SafetyCertificateOutlined,
    ArrowLeftOutlined,
    DownloadOutlined,
    ExpandOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { productResourcesApi, ProductResource, ResourceFolder } from '../../../services/productResourcesApi';

const { DirectoryTree } = Tree;

interface DataNode {
    title: string;
    key: string;
    isLeaf?: boolean;
    children?: DataNode[];
    icon?: React.ReactNode;
    data?: ProductResource | ResourceFolder;
}

const ProductResourcesViewer: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [resources, setResources] = useState<Record<string, ProductResource[]>>({});
    const [folders, setFolders] = useState<Record<string, ResourceFolder[]>>({});
    const [selectedResource, setSelectedResource] = useState<ProductResource | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loadingFile, setLoadingFile] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    // Cleanup blob URL when component unmounts or when resource changes
    useEffect(() => {
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [resData, foldersData] = await Promise.all([
                productResourcesApi.getResources(),
                productResourcesApi.getFolders()
            ]);
            setResources(resData);
            setFolders(foldersData);
        } catch (error) {
            console.error('Error fetching resources:', error);
        } finally {
            setLoading(false);
        }
    };

    const buildTree = (category: string): DataNode[] => {
        const categoryFolders = folders[category] || [];
        const categoryResources = resources[category] || [];

        const buildFolderNode = (folder: ResourceFolder): DataNode => {
            const childrenFolders = categoryFolders.filter(f => f.parent_folder_id === folder.id);
            const folderResources = categoryResources.filter(r => r.folder_id === folder.id);

            return {
                title: folder.name,
                key: `folder-${folder.id}`,
                icon: <FolderOutlined />,
                children: [
                    ...childrenFolders.map(buildFolderNode),
                    ...folderResources.map(r => ({
                        title: r.name,
                        key: `resource-${r.id}`,
                        isLeaf: true,
                        icon: <FilePdfOutlined style={{ color: '#ef4444' }} />,
                        data: r
                    }))
                ]
            };
        };

        const rootFolders = categoryFolders.filter(f => !f.parent_folder_id);
        const rootResources = categoryResources.filter(r => !r.folder_id);

        return [
            ...rootFolders.map(buildFolderNode),
            ...rootResources.map(r => ({
                title: r.name,
                key: `resource-${r.id}`,
                isLeaf: true,
                icon: <FilePdfOutlined style={{ color: '#ef4444' }} />,
                data: r
            }))
        ];
    };

    const loadResourceBlob = async (resource: ProductResource) => {
        setLoadingFile(true);
        try {
            // Revoke previous blob URL if exists
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
                setBlobUrl(null);
            }

            const blob = await productResourcesApi.downloadResource(resource.id);
            const url = URL.createObjectURL(blob);
            setBlobUrl(url);
            setSelectedResource(resource);
        } catch (error) {
            console.error('Error loading resource:', error);
            message.error('Erro ao carregar o ficheiro');
        } finally {
            setLoadingFile(false);
        }
    };

    const onSelect = (keys: React.Key[], info: any) => {
        if (info.node.isLeaf && info.node.data) {
            const resource = info.node.data as ProductResource;
            loadResourceBlob(resource);
        }
    };

    const handleBack = () => {
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
        }
        setBlobUrl(null);
        setSelectedResource(null);
    };

    const handleDownload = () => {
        if (blobUrl && selectedResource) {
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = selectedResource.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleOpenNewTab = () => {
        if (blobUrl) {
            window.open(blobUrl, '_blank');
        }
    };

    const renderCategoryTree = (category: string) => {
        if (loading) return <Spin />;

        const treeData = buildTree(category);

        if (treeData.length === 0) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem documentos" />;
        }

        return (
            <DirectoryTree
                multiple={false}
                defaultExpandAll
                onSelect={onSelect}
                treeData={treeData}
                className="resource-tree"
            />
        );
    };

    // If a resource is selected, show the preview
    if (selectedResource) {
        return (
            <div className="resource-preview">
                <div className="resource-preview-header">
                    <Button 
                        type="text" 
                        icon={<ArrowLeftOutlined />} 
                        onClick={handleBack}
                        className="resource-back-btn"
                    >
                        Voltar
                    </Button>
                    <div className="resource-preview-actions">
                        <Button 
                            type="text" 
                            icon={<ExpandOutlined />} 
                            onClick={handleOpenNewTab}
                            title="Abrir em nova aba"
                            disabled={!blobUrl}
                        />
                        <Button 
                            type="text" 
                            icon={<DownloadOutlined />} 
                            onClick={handleDownload}
                            title="Download"
                            disabled={!blobUrl}
                        />
                    </div>
                </div>
                <div className="resource-preview-title">
                    <FilePdfOutlined style={{ color: '#ef4444', marginRight: 8 }} />
                    {selectedResource.name}
                </div>
                <div className="resource-preview-frame">
                    {loadingFile ? (
                        <div className="resource-loading">
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
                            <span>A carregar ficheiro...</span>
                        </div>
                    ) : blobUrl ? (
                        <iframe
                            src={blobUrl}
                            title={selectedResource.name}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', borderRadius: '8px' }}
                        />
                    ) : (
                        <div className="resource-loading">
                            <span>Erro ao carregar</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="product-resources-viewer">
            <Tabs defaultActiveKey="telecomunicacao" size="small">
                <Tabs.TabPane tab={<span><WifiOutlined /> Telecom</span>} key="telecomunicacao">
                    {renderCategoryTree('telecomunicacao')}
                </Tabs.TabPane>
                <Tabs.TabPane tab={<span><ThunderboltOutlined /> Energia</span>} key="energia">
                    {renderCategoryTree('energia')}
                </Tabs.TabPane>
                <Tabs.TabPane tab={<span><SafetyCertificateOutlined /> Segurança</span>} key="seguranca">
                    {renderCategoryTree('seguranca')}
                </Tabs.TabPane>
            </Tabs>
        </div>
    );
};

export default ProductResourcesViewer;
